/**
 * "Ask AI" button and its answer card.
 *
 * The traffic watcher (nav/trafficWatch.ts) already offers a better route when
 * one appears, but it is on a four-minute timer and deliberately conservative -
 * it never nags. This is the other half: the driver who can SEE the queue ahead
 * and wants an opinion now, rather than waiting for the next tick.
 *
 * It re-asks the router from the live position and puts the fresh options, the
 * congestion figures and the driver's own history to the model. The answer may
 * well be "stay where you are" - that is a real answer here, not a failure, and
 * the card presents it as such.
 *
 * Everything is best-effort. A missing API key, the daily cap or a flaky
 * connection must never interrupt a drive: the card says so and closes.
 */

import type { RouteAdvice } from '../ai/advisor';
import type { Route } from '../core/types';
import { routeLetter } from '../core/format';
import { t } from '../core/i18n';

export type AskState =
  | { phase: 'idle' }
  | { phase: 'asking' }
  | { phase: 'answered'; advice: RouteAdvice; routes: Route[]; currentIndex: number }
  | { phase: 'error'; message: string };

export class AskAiButton {
  readonly button: HTMLElement;
  private card: HTMLElement;
  private state: AskState = { phase: 'idle' };

  constructor(
    parent: HTMLElement,
    private onAsk: (question: string | null) => void,
    /** Apply the recommended route. Only called for a real switch. */
    private onSwitch: (route: Route) => void,
  ) {
    this.button = document.createElement('button');
    this.button.className = 'ask-fab';
    this.button.hidden = true;
    this.button.setAttribute('aria-label', t('ask.title'));
    this.button.innerHTML = SPARK_SVG;
    parent.appendChild(this.button);
    this.button.addEventListener('click', () => this.ask(null));

    this.card = document.createElement('div');
    this.card.className = 'ask-card';
    this.card.hidden = true;
    parent.appendChild(this.card);
  }

  /** Shown only while navigating - there is nothing to re-decide otherwise. */
  setVisible(visible: boolean): void {
    this.button.hidden = !visible;
    if (!visible) this.close();
  }

  private ask(question: string | null): void {
    if (this.state.phase === 'asking') return;
    this.state = { phase: 'asking' };
    this.render();
    this.onAsk(question);
  }

  open(): void {
    this.ask(null);
  }

  /** Called by main once the router and model have answered. */
  show(advice: RouteAdvice, routes: Route[], currentIndex: number): void {
    this.state = { phase: 'answered', advice, routes, currentIndex };
    this.render();
  }

  fail(message: string): void {
    this.state = { phase: 'error', message };
    this.render();
  }

  close(): void {
    this.state = { phase: 'idle' };
    this.card.hidden = true;
    this.card.innerHTML = '';
    this.button.classList.remove('is-busy');
  }

  private render(): void {
    const st = this.state;
    this.button.classList.toggle('is-busy', st.phase === 'asking');

    if (st.phase === 'idle') {
      this.card.hidden = true;
      this.card.innerHTML = '';
      return;
    }
    this.card.hidden = false;

    if (st.phase === 'asking') {
      this.card.innerHTML = `
        <div class="ask-card__row">
          <span class="ask-card__spinner" aria-hidden="true"></span>
          <span class="ask-card__headline">${t('ask.thinking')}</span>
        </div>`;
      return;
    }

    if (st.phase === 'error') {
      this.card.innerHTML = `
        <div class="ask-card__row">
          <span class="ask-card__headline">${escapeHtml(st.message)}</span>
        </div>
        <div class="ask-card__actions">
          <button class="btn btn--ghost btn--small" data-act="close">${t('common.close')}</button>
        </div>`;
      this.wire();
      return;
    }

    // Answered. pick === -1 means "stay put", which needs no switch button -
    // offering one anyway would invite the driver to do the thing the advice
    // just told them not to.
    const { advice, routes, currentIndex } = st;
    const stay = advice.pick < 0 || advice.pick === currentIndex;
    const target = stay ? null : routes[advice.pick];

    this.card.innerHTML = `
      <div class="ask-card__row">
        <span class="ask-card__badge ${stay ? 'is-stay' : 'is-switch'}">
          ${stay ? CHECK_SVG : SWITCH_SVG}
        </span>
        <span class="ask-card__headline">${escapeHtml(advice.headline)}</span>
      </div>
      <p class="ask-card__reason">${escapeHtml(advice.reason)}</p>
      ${
        advice.tips.length
          ? `<ul class="ask-card__tips">${advice.tips
              .map((tip) => `<li>${escapeHtml(tip)}</li>`)
              .join('')}</ul>`
          : ''
      }
      <div class="ask-card__actions">
        ${
          target
            ? `<button class="btn btn--primary btn--small" data-act="switch">
                 ${t('ask.switch', { letter: routeLetter(advice.pick) })}
               </button>`
            : ''
        }
        <button class="btn btn--ghost btn--small" data-act="close">
          ${stay ? t('ask.gotIt') : t('ask.keep')}
        </button>
      </div>`;
    this.wire(target);
  }

  private wire(target?: Route | null): void {
    this.card.querySelector('[data-act=close]')?.addEventListener('click', () => this.close());
    this.card.querySelector('[data-act=switch]')?.addEventListener('click', () => {
      if (target) this.onSwitch(target);
      this.close();
    });
  }
}

const SPARK_SVG = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3 L13.6 9.2 L19.5 11 L13.6 12.8 L12 19 L10.4 12.8 L4.5 11 L10.4 9.2 Z" fill="currentColor"/><circle cx="18.5" cy="5.5" r="1.6" fill="currentColor"/></svg>`;
const CHECK_SVG = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5 L10 17.5 L19 7" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const SWITCH_SVG = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 8 h11 l-3 -3 M20 16 h-11 l3 3" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}
